import { useState } from 'react';
import { cn } from '@/lib/cn';
import { formatPaise, timeAgo } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import type { AdminRefundRow, RefundStatus } from '@shared/types/api';
import {
  useAdminRefunds,
  useApproveRefund,
  useMarkRefundedOffline,
  useRejectRefund,
} from '@/hooks/use-admin';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

const TABS: { value: RefundStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'INITIATED', label: 'Initiated' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'FAILED', label: 'Failed' },
];

const TONE: Record<RefundStatus, string> = {
  REQUESTED: 'bg-amber-50 text-amber-800',
  APPROVED: 'bg-blue-50 text-blue-800',
  INITIATED: 'bg-indigo-50 text-indigo-800',
  COMPLETED: 'bg-brand-50 text-brand-800',
  REJECTED: 'bg-red-50 text-red-700',
  FAILED: 'bg-red-50 text-red-700',
};

// Heuristic: if providerRefundId starts with `offline-`, this refund is on an
// offline-paid invoice. Used to pick between the approve/reject (online) vs
// mark-refunded-offline (offline) action.
function isOffline(r: AdminRefundRow): boolean {
  return !!r.providerRefundId && r.providerRefundId.startsWith('offline-');
}

type OpenPanel =
  | { kind: 'approve'; row: AdminRefundRow }
  | { kind: 'reject'; row: AdminRefundRow }
  | { kind: 'offline'; row: AdminRefundRow }
  | null;

export function AdminRefundsPage() {
  const [tab, setTab] = useState<RefundStatus | 'ALL'>('REQUESTED');
  const [panel, setPanel] = useState<OpenPanel>(null);
  const { canEdit, canDelete, isSuperAdmin } = useSectionPermission('REFUNDS');
  const hasAnyAction = canEdit || canDelete || isSuperAdmin;
  const { data, isLoading } = useAdminRefunds({
    status: tab === 'ALL' ? undefined : tab,
    pageSize: 50,
  });
  const items = data?.items ?? [];

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <h1 className="text-xl font-semibold text-gray-900">Refunds</h1>
      <p className="text-sm text-gray-600">
        Approve / reject refund requests. For refunds on <strong>offline</strong>-paid invoices,
        transfer the money back manually and record the bank reference here.
      </p>

      <div className="mt-5 flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap border-b border-gray-200 pb-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
              tab === t.value
                ? 'bg-brand-700 text-white shadow-xs'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/60 sm:border-0',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[750px] text-sm text-left">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Refund</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Tx</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Paid</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Eligible</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Stage %</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Status</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Created</th>
              {hasAnyAction ? <th className="px-4 py-3 text-right font-medium pr-6 whitespace-nowrap">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={hasAnyAction ? 8 : 7} className="p-6 text-center text-gray-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={hasAnyAction ? 8 : 7} className="p-6 text-center text-gray-500">No refund requests.</td></tr>
            ) : items.map((r) => {
              const offline = isOffline(r);
              return (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap font-medium text-gray-900">{r.id.slice(0, 8)}…</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{r.transactionId.slice(0, 8)}…</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatPaise(r.paidAmountPaise)}</td>
                  <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatPaise(r.eligibleRefundPaise)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{r.stageRefundPercentage}%</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase', TONE[r.status])}>
                      {r.status}
                    </span>
                    {offline ? (
                      <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[9px] uppercase text-gray-600">
                        Offline
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{timeAgo(r.createdAt)}</td>
                  {hasAnyAction ? (
                    <td className="px-4 py-3 text-right whitespace-nowrap pr-6">
                      <div className="flex flex-wrap justify-end gap-2 text-xs">
                        {r.status === 'REQUESTED' && offline && (canEdit || isSuperAdmin) ? (
                          <button
                            className="font-medium text-brand-700 hover:underline"
                            onClick={() => setPanel({ kind: 'offline', row: r })}
                          >
                            Mark refunded (offline)
                          </button>
                        ) : null}
                        {r.status === 'REQUESTED' && !offline ? (
                          <>
                            {(canEdit || isSuperAdmin) && (
                              <button
                                className="text-brand-700 hover:underline"
                                onClick={() => setPanel({ kind: 'approve', row: r })}
                              >
                                Approve
                              </button>
                            )}
                            {(canDelete || isSuperAdmin) && (
                              <button
                                className="text-red-600 hover:underline"
                                onClick={() => setPanel({ kind: 'reject', row: r })}
                              >
                                Reject
                              </button>
                            )}
                          </>
                        ) : null}
                        {r.rejectionReason ? (
                          <span className="text-gray-400" title={r.rejectionReason}>note</span>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </section>

      {panel?.kind === 'approve' ? (
        <ApprovePanel row={panel.row} onClose={() => setPanel(null)} />
      ) : null}
      {panel?.kind === 'reject' ? (
        <RejectPanel row={panel.row} onClose={() => setPanel(null)} />
      ) : null}
      {panel?.kind === 'offline' ? (
        <OfflineRefundPanel row={panel.row} onClose={() => setPanel(null)} />
      ) : null}
    </div>
  );
}

function ApprovePanel({ row, onClose }: { row: AdminRefundRow; onClose: () => void }) {
  const [finalPaise, setFinalPaise] = useState(row.eligibleRefundPaise);
  const [notes, setNotes] = useState('');
  const mut = useApproveRefund();
  return (
    <Dialog
      open
      onClose={onClose}
      title="Approve refund (online)"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() =>
              mut.mutate(
                { id: row.id, finalRefundPaise: finalPaise, notes: notes.trim() || undefined },
                { onSuccess: onClose },
              )
            }
            disabled={mut.isPending}
          >
            {mut.isPending ? 'Approving…' : 'Approve + fire Razorpay'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-700">
        This fires a Razorpay refund. Status moves to INITIATED; the webhook will close it to
        COMPLETED.
      </p>
      <div className="mt-3 space-y-3 text-sm">
        <L label="Final refund (paise)">
          <Input
            inputMode="numeric"
            value={finalPaise}
            onChange={(e) => setFinalPaise(e.target.value.replace(/\D/g, ''))}
          />
          <p className="mt-1 text-xs text-gray-500">
            Defaults to the policy-derived eligible amount ({formatPaise(row.eligibleRefundPaise)}).
          </p>
        </L>
        <L label="Notes (optional)">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </L>
        {mut.isError ? <p className="text-sm text-red-600">{apiErrorMessage(mut.error)}</p> : null}
      </div>
    </Dialog>
  );
}

function RejectPanel({ row, onClose }: { row: AdminRefundRow; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const mut = useRejectRefund();
  return (
    <Dialog
      open
      onClose={onClose}
      title="Reject refund"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => mut.mutate({ id: row.id, reason: reason.trim() }, { onSuccess: onClose })}
            disabled={reason.trim().length < 1 || mut.isPending}
          >
            {mut.isPending ? 'Rejecting…' : 'Confirm'}
          </Button>
        </>
      }
    >
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (shown back to the client)"
      />
      {mut.isError ? <p className="mt-2 text-sm text-red-600">{apiErrorMessage(mut.error)}</p> : null}
    </Dialog>
  );
}

function OfflineRefundPanel({ row, onClose }: { row: AdminRefundRow; onClose: () => void }) {
  const [bankReference, setBankReference] = useState('');
  const [finalPaise, setFinalPaise] = useState(row.eligibleRefundPaise);
  const [notes, setNotes] = useState('');
  const mut = useMarkRefundedOffline();

  return (
    <Dialog
      open
      onClose={onClose}
      title="Mark refund paid (offline)"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() =>
              mut.mutate(
                {
                  id: row.id,
                  bankReference: bankReference.trim(),
                  finalRefundPaise: finalPaise,
                  notes: notes.trim() || undefined,
                },
                { onSuccess: onClose },
              )
            }
            disabled={bankReference.trim().length < 3 || mut.isPending}
          >
            {mut.isPending ? 'Saving…' : 'Confirm refund paid'}
          </Button>
        </>
      }
    >
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <div className="font-medium">Transfer this amount back to the client:</div>
        <div className="mt-1 text-lg font-semibold">{formatPaise(finalPaise)}</div>
        <div className="mt-1 text-xs">
          Paid original: {formatPaise(row.paidAmountPaise)} · Stage %: {row.stageRefundPercentage}
        </div>
      </div>

      <div className="mt-3 space-y-3 text-sm">
        <L label="Bank / UPI reference *">
          <Input
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
            placeholder="UTR1234567890"
          />
        </L>
        <L label="Adjust amount (paise)">
          <Input
            inputMode="numeric"
            value={finalPaise}
            onChange={(e) => setFinalPaise(e.target.value.replace(/\D/g, ''))}
          />
        </L>
        <L label="Notes (optional)">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </L>
        {mut.isError ? <p className="text-sm text-red-600">{apiErrorMessage(mut.error)}</p> : null}
      </div>
    </Dialog>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
    </label>
  );
}
